import {Injectable, inject} from '@angular/core';
import {AlertController, Platform} from '@ionic/angular';
import {Logtail} from '@logtail/browser';
import {environment} from 'src/environments/environment';
import {AuthService} from './auth.service';

export type LogContext = Record<string, unknown>;

@Injectable({
  providedIn: 'root',
})
export class LoggerService {
  private alertController = inject(AlertController);
  private authService = inject(AuthService);
  private platform = inject(Platform);
  // Local dev has no real Logtail token (an empty one crashes bootstrap, so
  // environment.local.ts carries a stub) — sending would just 401 against
  // Betterstack and spam console.error on every flush. Skip remote logging.
  private logger = environment.ENV_NAME === 'local' ? null : new Logtail(environment.LOGTAIL);

  private payload(context?: LogContext) {
    return {
      app: 'mobile-app',
      env: environment.ENV_NAME,
      context: {
        user_id: this.authService.authUser?.id,
        platform: this.platform.is('ios') ? 'ios' : this.platform.is('android') ? 'android' : 'web',
        ...context,
      },
    };
  }

  logError(message: string, context?: LogContext): void {
    try {
      this.logger?.error(message, this.payload(context));
      this.logger?.flush();
    } catch {
      // logging must never break the app
    }
  }

  logInformation(message: string, context?: LogContext): void {
    try {
      this.logger?.info(message, this.payload(context));
      this.logger?.flush();
    } catch {
      // logging must never break the app
    }
  }

  logErrorAndAlert(error: any, context?: LogContext): void {
    this.logError(`An error occurred: ${error?.message ?? error}`, {
      ...context,
      stack: error?.stack,
    });
    this.presentAlert().catch(() => {
      // alert failure must not break logging
    });
  }

  async presentAlert() {
    const alert = await this.alertController.create({
      header: 'Error!',
      message: 'An error occurred. Please try again later.',
      buttons: ['OK'],
    });

    await alert.present();
  }
}
